import React, { createContext, useContext } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { MARKERS } from '../../usage-tracking/usageKeys';
import type { ContractType } from '../../../types/ContractType.types';

// RITA-1526 — Spalte „Logo“ der Vertragsarten-Tabelle (Einstellungen > Vertragsarten).
//
// Das Grid reicht Formatter und Editor die ganze Zeile; neue, noch nicht gespeicherte Zeilen
// haben keine id. Deshalb ist der Ablauf zweistufig: erst Vertragsart speichern, dann Logo.
// Der Klick auf die Zelle öffnet den Logo-Dialog über einen Kontext, den die Tabelle bereitstellt —
// so bleibt die Formatter-Komponente modulweit stabil (das Grid montiert Zellen sonst bei jeder
// Darstellung neu, siehe Hotspot „stabile Rückrufe“).

/** Zeile, wie das Grid sie übergibt: gespeicherte Vertragsart oder neue Zeile ohne id. */
export type ContractTypeLogoRow = Partial<ContractType>;

export const CONTRACT_TYPE_LOGO_NONE_TEXT = 'kein Logo';
export const CONTRACT_TYPE_LOGO_SAVE_FIRST_TEXT = 'nach dem Speichern';

/**
 * Öffnet den Logo-Dialog für eine gespeicherte Vertragsart; die Tabelle stellt ihn bereit.
 * null, solange die Vertragsarten nicht geladen sind: dann zeigt die Zelle keinen Knopf
 * (die Platzhalterzeile der Tabelle trägt eine echte id, darf aber nichts öffnen).
 */
export const ContractTypeLogoDialogContext = createContext<((contractType: ContractType) => void) | null>(null);

export const contractTypeLogoButtonLabel = (row: ContractTypeLogoRow): string =>
  row.logo_data_url ? `Logo für ${row.title ?? ''} ändern` : `Logo für ${row.title ?? ''} hinzufügen`;

const isSavedRow = (row: ContractTypeLogoRow | undefined): row is ContractTypeLogoRow & { id: number } =>
  !!row && typeof row.id === 'number';

const toContractType = (row: ContractTypeLogoRow & { id: number }): ContractType => ({
  id: row.id,
  title: row.title ?? '',
  is_default: row.is_default ?? null,
  logo_data_url: row.logo_data_url ?? null,
});

function SaveFirstHint() {
  return (
    <Typography variant="caption" color="text.secondary" data-testid="contract-type-logo-save-first">
      {CONTRACT_TYPE_LOGO_SAVE_FIRST_TEXT}
    </Typography>
  );
}

function LogoPreview({ row }: { row: ContractTypeLogoRow }) {
  if (!row.logo_data_url) {
    return (
      <Typography variant="body2" color="text.secondary">
        {CONTRACT_TYPE_LOGO_NONE_TEXT}
      </Typography>
    );
  }
  return (
    <Box
      component="img"
      src={row.logo_data_url}
      alt={`Logo ${row.title ?? ''}`}
      sx={{ maxHeight: 32, maxWidth: 96, objectFit: 'contain', display: 'block' }}
    />
  );
}

/** Das Grid übergibt außerdem value/column; hier zählt nur die Zeile. */
interface CellProps {
  row?: ContractTypeLogoRow;
}

/** Anzeige in der Tabelle: kleine Vorschau oder „kein Logo“; Klick öffnet den Dialog. */
export function ContractTypeLogoFormatter({ row }: CellProps) {
  const openDialog = useContext(ContractTypeLogoDialogContext);
  if (!isSavedRow(row)) return <SaveFirstHint />;
  if (!openDialog) return <LogoPreview row={row} />;
  const label = contractTypeLogoButtonLabel(row);
  return (
    <ButtonBase
      onClick={() => openDialog(toContractType(row))}
      aria-label={label}
      title={label}
      data-usage={MARKERS.einstellungenVertragsartenLogoOeffnen}
      data-testid="contract-type-logo-cell"
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: 36,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <LogoPreview row={row} />
    </ButtonBase>
  );
}

/** In der Bearbeitungszeile nur lesend: neue Zeilen bekommen den Hinweis auf das Speichern. */
export function ContractTypeLogoEditor({ row }: CellProps) {
  if (!isSavedRow(row)) return <SaveFirstHint />;
  return <LogoPreview row={row} />;
}
